<script lang="ts">
  // The hovered-card inspector: big card art + name + type line + ATK/DEF + description. Extracted from
  // Duel.svelte so it can ALSO be rendered in isolation in the Texture Pack editor. Each core element
  // carries a stable .tp-cardinfo-* class a texture pack can target with any CSS.
  import { fade } from "svelte/transition";
  import { cardImageLarge } from "../../../data.ts";
  import type { QueriedCard } from "@ygoevo/protocol";

  interface Info { name?: string; category?: "monster" | "monster-extra" | "spell" | "trap" | "token"; attribute?: string | null; race?: string | null; types: string[]; desc?: string; level?: number | null }
  let { card, info }: { card: QueriedCard | null; info: Info | null } = $props();

  // OCGcore includes ATTACK/DEFENSE in every field query and reports 0/0 for Spells/Traps. Type is
  // authoritative while a card is live; catalog category is the fallback for preview/test data.
  const isMonster = $derived(!!card && (card.type !== undefined && card.type !== 0
    ? (card.type & 0x1) !== 0
    : info?.category === "monster" || info?.category === "monster-extra" || info?.category === "token"));
</script>

<div class="tp-cardinfo-body relative min-h-0 flex-1 px-3.5 py-3">
  {#if card && card.code}
    {#key card.code}
      <div class="tp-cardinfo-content flex h-full flex-col gap-3" in:fade={{ duration: 120 }}>
        <!-- Phone: the stacked layout squashed the description to ~zero on a short landscape viewport
             ("can't read card effects"). There this wrapper becomes a compact HEADER ROW — thumbnail
             art beside the name/level/type/stats column — so the description below keeps real,
             scrollable height. Desktop is untouched: display:contents = no layout change. -->
        <div class="contents phone:flex phone:min-h-0 phone:shrink-0 phone:items-start phone:gap-2.5">
          <div class="tp-cardinfo-art aspect-[59/86] w-full max-h-[48%] shrink-0 overflow-hidden chamfer ring-1 ring-cyan/35" style="--c:8px;box-shadow:0 0 22px color-mix(in oklab,var(--color-cyan) 14%,transparent)">
            <img src={cardImageLarge(card.code)} alt={info?.name ?? "card"} class="size-full object-cover" draggable="false" />
          </div>
          <div class="contents phone:flex phone:min-w-0 phone:flex-1 phone:flex-col phone:gap-1">
            <div class="tp-cardinfo-name shrink-0 font-display text-[1.05rem] font-bold leading-[1.2] text-ink">{info?.name ?? `Card #${card.code}`}</div>
            <!-- level stars + attribute chip (Master-Duel style), monsters carry a level, spell/traps don't -->
            {#if info?.level || info?.attribute}
              <div class="tp-cardinfo-level flex shrink-0 items-center gap-2">
                {#if info?.attribute}<span class="chamfer bg-cyan/12 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-cyan" style="--c:3px">{info.attribute}</span>{/if}
                {#if info?.level}<span class="truncate text-[0.82rem] leading-none text-gold" title="Level {info.level}" style="text-shadow:0 0 6px color-mix(in oklab,var(--color-gold) 40%,transparent)">{"★".repeat(Math.min(info.level, 12))}</span>{/if}
              </div>
            {/if}
            <div class="tp-cardinfo-type shrink-0 truncate text-[0.68rem] uppercase tracking-wide text-cyan-dim">
              {#if info}{#if info.race}{info.race} · {/if}{info.types.join("/")}{/if}
            </div>
            {#if isMonster}
              <div class="tp-cardinfo-stats flex shrink-0 items-center gap-3 font-display text-[0.92rem] font-bold tabular-nums">
                <span class="text-gold">ATK {card.attack === -2 ? "?" : card.attack}</span>
                <span class="text-cyan">DEF {card.defense === -2 ? "?" : (card.defense ?? 0)}</span>
              </div>
            {/if}
          </div>
        </div>
        <div class="tp-cardinfo-desc min-h-0 flex-1 overflow-y-auto scroll-cyber overscroll-contain pr-1 text-[0.72rem] leading-relaxed text-ink-dim/85">{info?.desc ?? ""}</div>
      </div>
    {/key}
  {:else}
    <div class="grid h-full place-items-center text-center font-display text-[0.66rem] uppercase leading-relaxed tracking-[0.18em] text-ink-dim/35"><span class="touch:hidden">Hover a card<br />to inspect</span><span class="hidden touch:inline">Tap a card<br />to inspect</span></div>
  {/if}
</div>
